/**
 * EVENT PARSER (MEV5)
 * Translates raw database events into standardized metabolic flux inputs.
 */

import { toLogicDate } from '@/utils/date-utils';
import { ClinicalFraction } from './types';
import { SequencingResolver, SequencingStrategy } from './resolvers/sequencing-resolver';

export interface ParsedMeal {
    startUnix: number;
    durationMins: number;
    rate: number;
    delayMod: number;
    totalCalories: number; // For Dynamic Trituration (Phase 3.2)
    fractions: Record<ClinicalFraction, number>;
    insulinLoad: number;
}

export interface ParsedWorkout {
    startUnix: number;
    endUnix: number;
    rpe: number; // 1-10
}

export const EventParser = {
    
    /**
     * Extracts and standardizes all valid events for the 48-hour simulation window.
     */
    parseEvents(events: any[], targetDateStr: string, midnightUnix: number) {
        const yesterdayLocal = new Date(midnightUnix - 86400000);
        const yStr = toLogicDate(yesterdayLocal);

        const filterByDate = (types: string[]) => events.filter(e => {
            const t = e.type?.toUpperCase();
            if (!types.includes(t)) return false;
            const dateStr = toLogicDate(e.start_time);
            return dateStr === yStr || dateStr === targetDateStr;
        });

        const rawMeals = filterByDate(['NUTRITION', 'NUTRITION_LOG', 'SUPPLEMENT', 'SUPPLEMENTATION']);
        const rawWorkouts = filterByDate(['PERFORMANCE', 'PERFORMANCE_LOG', 'WORKOUT']);

        const meals = rawMeals.map(m => this.parseMeal(m));
        const workouts = rawWorkouts.map(w => this.parseWorkout(w));

        return { meals, workouts };
    },

    /**
     * Extracts and standardizes events for a specific Unix window (Multi-day support).
     */
    parseEventsWindow(events: any[], startUnix: number, endUnix: number) {
        const filterByWindow = (types: string[]) => events.filter(e => {
            const t = e.type?.toUpperCase();
            if (!types.includes(t)) return false;
            const eventUnix = new Date(e.start_time).getTime();
            return eventUnix >= startUnix && eventUnix <= endUnix;
        });

        const rawMeals = filterByWindow(['NUTRITION', 'NUTRITION_LOG', 'SUPPLEMENT', 'SUPPLEMENTATION']);
        const rawWorkouts = filterByWindow(['PERFORMANCE', 'PERFORMANCE_LOG', 'WORKOUT']);

        const meals = rawMeals.map(m => this.parseMeal(m));
        const workouts = rawWorkouts.map(w => this.parseWorkout(w));

        return { meals, workouts };
    },

    /**
     * Decomposes a raw meal event into specific clinical fractions.
     */
    parseMeal(meal: any): ParsedMeal {
        const start = new Date(meal.start_time).getTime();
        const end = new Date(meal.end_time || meal.start_time).getTime();
        const duration = Math.max(15, (end - start) / 60000);
        const rate = 1 / duration;

        const data = { ...(meal.payload || {}), ...(meal.preview_data || {}) };
        const strategy: SequencingStrategy = data.sequencing_strategy || 'MIXED';
        const delayMod = SequencingResolver.getCarbDelayMod(strategy);

        const cal = Number(data.calories?.total || data.calories || 0);
        const c = Number(data.carbs?.total || data.carbs || 0);
        const p = Number(data.protein?.total || data.protein || 0);
        const f = Number(data.fat?.total || data.fat || 0);
        const fib = Number(data.micros?.fiber?.amount || 0);
        const ins = Number(data.insulin_load?.total || data.insulin_load || 0);

        const micros = data.micros || {};
        const g = Number(micros.glucose?.amount || 0);
        const fr = Number(micros.fructose?.amount || 0);
        const s = Number(micros.sucrose?.amount || 0);
        const l = Number(micros.lactose?.amount || 0);
        const cx = Number(micros.complex_carbs?.amount || 0);
        const ip = Number(micros.intact_protein?.amount || 0);
        const fa = Number(micros.free_amino_acids?.amount || 0);
        const cp = Number(micros.collagen_peps?.amount || 0);
        const lip = Number(micros.lipids?.amount || 0);

        // Cleavage & Decomposition
        const fractions = {
            glucose: (g + s*0.5 + l*0.5),
            fructose: (fr + s*0.5 + l*0.5),
            complex_carbs: (cx + Math.max(0, c - (g+fr+s+l+cx))),
            intact_protein: (ip || p),
            free_amino_acids: fa,
            collagen_peps: cp,
            lipids: (lip || f),
            fiber: fib,
            sucrose: 0, // Cleaved
            lactose: 0  // Cleaved
        };

        return {
            startUnix: start,
            durationMins: duration,
            rate,
            delayMod,
            totalCalories: cal,
            fractions,
            insulinLoad: ins
        };
    },

    /**
     * Extracts physical intensity data from a workout event.
     */
    parseWorkout(workout: any): ParsedWorkout {
        const start = new Date(workout.start_time).getTime();
        const end = new Date(workout.end_time || start + 3600000).getTime();
        const rpe = Number(workout.payload?.rpe || 7);

        return { startUnix: start, endUnix: end, rpe };
    }
};
